import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import api from "@/lib/api";

/**
 * Shows the most recent volunteer signups in a compact card at the top of admin.
 * Auto-refresh every 60s. Click a row to jump to the submissions detail below.
 */
export default function VolunteerSignupsCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetch_ = async () => {
    try {
      const r = await api.get("/admin/volunteer-signups?limit=50");
      setData(r.data);
    } catch { setData(null); }
  };

  useEffect(() => {
    fetch_();
    const t = setInterval(fetch_, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const rows = data.volunteers || [];
  const cap = expanded ? rows.length : 5;
  const visible = rows.slice(0, cap);

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  const primaryLabel = (primary_role) => {
    // primary_role is stored as either a single string, or semicolon-joined multi-choice text.
    if (!primary_role) return "—";
    const parts = primary_role.split(";").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return "—";
    // Compact each choice
    return parts.map((p) => {
      if (p.startsWith("Friday")) return "Fri Setup";
      if (p.startsWith("Saturday")) return "Sat Event";
      if (p.startsWith("Remote")) return "Remote/Admin";
      return p.slice(0, 24);
    }).join(" · ");
  };

  return (
    <div data-testid="volunteer-signups-card" className="rounded-2xl border border-emerald-100 bg-white shadow-sm mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-emerald-50 bg-gradient-to-r from-emerald-50/70 via-white to-amber-50/40">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-emerald-700 text-white flex items-center justify-center">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <div className="font-[Outfit] text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-bold">Volunteer Signups</div>
            <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">
              <span className="tabular-nums">{data.total ?? 0}</span> total · <span className="text-emerald-700">{rows.length} recent</span>
            </div>
          </div>
        </div>
        {rows.length > 5 && (
          <button data-testid="volunteers-toggle" onClick={() => setExpanded((v) => !v)} className="text-[11px] font-[Outfit] font-semibold text-emerald-700 hover:text-emerald-900">
            {expanded ? "Show less" : `Show all ${rows.length}`}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-6 text-sm text-emerald-800/60 font-[Outfit] italic text-center">
          No volunteer signups yet — the recruitment campaign will drive people here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-[Outfit]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-emerald-900/60 font-bold border-b border-emerald-50 bg-emerald-50/30">
                <th className="px-5 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Alumni</th>
                <th className="px-4 py-2 text-right">Signed Up</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((v, i) => (
                <tr key={v.id || i} data-testid={`volunteer-row-${i}`} className="border-b border-emerald-50 hover:bg-emerald-50/30">
                  <td className="px-5 py-2 text-emerald-950 font-semibold whitespace-nowrap">{v.name}</td>
                  <td className="px-4 py-2 text-emerald-800">
                    <a href={`mailto:${v.email}`} className="hover:underline">{v.email}</a>
                  </td>
                  <td className="px-4 py-2 text-emerald-800/80 whitespace-nowrap tabular-nums">{v.phone || "—"}</td>
                  <td className="px-4 py-2 text-emerald-900">{primaryLabel(v.primary_role)}</td>
                  <td className="px-4 py-2">
                    {v.is_alumni === "Yes" ? (
                      <span className="inline-flex text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap">
                        Alumni {v.alumni_year && `· ${v.alumni_year}`}
                      </span>
                    ) : v.is_out_of_state === "Yes" ? (
                      <span className="inline-flex text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300">
                        Out-of-state
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-emerald-800/70 whitespace-nowrap tabular-nums">{fmt(v.submitted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
